/**
 * The Forge's default copy (the rule is in `shared.ts`): the words its three pages say, by
 * section (browsing the boxes shoppers built, the builder, the creator's own boxes), and the one
 * word another page says too: the box page names a forge box's creator after
 * `FORGE_CREATOR_LABEL`.
 * The builder's arithmetic and what its submit still needs are `forgeBuilder.ts`. The username
 * gate, why a box can't be edited, what is wrong with a typed name, the toasts and every refused
 * write are the platform's and stay with the storefront.
 */
import { FORGE_MAX_ITEMS, FORGE_MIN_ITEMS, FORGE_NAME_MAX, type ForgeBoxSort } from "../forge.js";
import type { ForgeSubmitKey } from "../forgeBuilder.js";
import type { MyForgeBox } from "../index.js";
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import { STOREFRONT_FRAME_COPY } from "./frame.js";
import { STOREFRONT_PAGING_COPY } from "./shared.js";

/** Before a forge box's creator's name, which a forge box's page says too. */
export const FORGE_CREATOR_LABEL = "Built by";

/**
 * The links the Forge's pages share, so a link and the page it opens always agree: Home, the
 * Forge, the creator's own boxes (that page's title too) and the builder (its ready submit too).
 */
function forgeLinks(terms: StorefrontTerminology) {
  const { box, forge } = terms;
  return {
    home: STOREFRONT_FRAME_COPY.home,
    forge: capitalizeTerm(forge.singular),
    mine: `My ${box.plural}`,
    create: `Create ${box.singular}`,
  };
}

/** The Forge's browse page's words, by section, in the store's terms. */
export interface ForgeCopy {
  heading: {
    /** The breadcrumb's first stop. */
    home: string;
    /** The Forge's name: the page's title, and its place in the trail. */
    title: string;
    intro: string;
    /** Signed in: the way to the shopper's own boxes, and to the builder. */
    mine: string;
    create: string;
    /** Signed out: the way to sign in first. */
    signIn: string;
  };
  /** The tab list's accessible name, and each sort's tab. */
  sort: { label: string; options: Record<ForgeBoxSort, string> };
  grid: {
    /** The grid's heading, read by screen readers. */
    title: string;
    /** Under a box's name: who built it. */
    byline: (creator: string) => string;
    /** While nobody has built one. */
    empty: string;
    /** The next page's button. */
    loadMore: string;
  };
}

export function defaultForgeCopy(terms: StorefrontTerminology): ForgeCopy {
  const { box, item, forge } = terms;
  const links = forgeLinks(terms);
  return {
    heading: {
      home: links.home,
      title: links.forge,
      intro: `${capitalizeTerm(box.plural)} built by players: pick the ${item.plural}, set the odds, and earn when others open them.`,
      mine: links.mine,
      create: links.create,
      signIn: `Sign in to create ${box.plural}`,
    },
    sort: {
      label: `Sort ${box.plural}`,
      options: { new: "Newest", popular: "Most opened" },
    },
    grid: {
      title: `${capitalizeTerm(forge.singular)} ${box.plural}`,
      byline: (creator) => `${FORGE_CREATOR_LABEL} ${creator}`,
      empty: `No ${box.plural} yet. Be the first to build one.`,
      loadMore: STOREFRONT_PAGING_COPY.loadMore,
    },
  };
}

/** The builder's heading for one of its modes: its place in the trail, its title and intro. */
export interface ForgeBuilderModeCopy {
  current: string;
  title: string;
  intro: string;
}

/** The Forge builder's words, by section, in the store's terms, for creating or editing. */
export interface ForgeBuilderCopy {
  heading: {
    /** The trail's stops above the builder: Home, then the Forge (also the phones' Back). */
    home: string;
    forge: string;
    create: ForgeBuilderModeCopy;
    edit: ForgeBuilderModeCopy;
  };
  details: {
    /** The step's eyebrow. */
    step: string;
    /** The step's title, with the image when the store offers images, else the name alone. */
    title: { withImage: string; nameOnly: string };
    nameLabel: string;
    namePlaceholder: string;
    /** The name's rules, under the field. */
    nameHelper: string;
    imageLabel: string;
    /** A preset image's name, counted from 1. */
    imageOption: (position: number) => string;
  };
  picker: {
    step: string;
    title: string;
    /** Under the title: how many of the most a box holds are picked. */
    count: (picked: number) => string;
    /** The search field's label and placeholder. */
    search: string;
    /** The value range's fields. */
    min: { label: string; placeholder: string };
    max: { label: string; placeholder: string };
    /** The group chips' accessible name, and each chip. */
    groupLabel: string;
    groups: { all: string; bulk: string };
    /** Over the rest of the items, while the Mystery Bulk Cards lead the list. */
    itemsTitle: string;
    /** When the search matched nothing. */
    empty: string;
    loadMore: string;
  };
  rates: {
    step: string;
    title: string;
    description: string;
    /** Names the total, read before it. */
    totalLabel: string;
    /** In place of the rows while fewer than two are picked. */
    empty: string;
    /** The value sort's label, and its two directions. */
    sortLabel: string;
    ascending: string;
    descending: string;
    /** A row's pull-rate field, named for its item; the placeholder shows the format. */
    rateLabel: (itemName: string) => string;
    ratePlaceholder: string;
    remove: (itemName: string) => string;
  };
  summary: {
    step: string;
    title: Record<"create" | "edit", string>;
    /** The creator's cut, and the sentence around what it earns per open. */
    cutLabel: string;
    earnings: { before: string; after: string };
    priceLabel: string;
    /** The submit: the first thing the box still needs, else the action. */
    submit: Record<ForgeSubmitKey, string>;
    /** The submit while the box saves. */
    saving: string;
    cancel: string;
  };
}

export function defaultForgeBuilderCopy(terms: StorefrontTerminology): ForgeBuilderCopy {
  const { box, item } = terms;
  const Box = capitalizeTerm(box.singular);
  const links = forgeLinks(terms);
  return {
    heading: {
      home: links.home,
      forge: links.forge,
      create: {
        current: links.create,
        title: `Build your own ${box.singular}`,
        intro: `Name it, pick the ${item.plural}, set each pull rate. The price follows from the odds, and every open is provably fair.`,
      },
      edit: {
        current: `Edit ${box.singular}`,
        title: `Edit ${box.singular}`,
        intro: "Change the pull rates or your cut. The name and image stay as they are.",
      },
    },
    details: {
      step: "Step 1",
      title: { withImage: "Name and image", nameOnly: "Name" },
      nameLabel: `${Box} name`,
      namePlaceholder: `Name your ${box.singular}`,
      nameHelper: `Up to ${FORGE_NAME_MAX} characters: letters, numbers and spaces. No profanity.`,
      imageLabel: "Image",
      imageOption: (position) => `Use image ${position}`,
    },
    picker: {
      step: "Step 2",
      title: `Pick ${item.plural}`,
      count: (picked) => `${picked} of up to ${FORGE_MAX_ITEMS} selected.`,
      search: `Search ${item.plural}`,
      min: { label: "Minimum value", placeholder: "Min value" },
      max: { label: "Maximum value", placeholder: "Max value" },
      groupLabel: `Show ${item.plural}`,
      // The platform's shared tier rows are all named Mystery Bulk Card, whatever the store's term.
      groups: { all: `All ${item.plural}`, bulk: "Mystery Bulk Cards" },
      itemsTitle: capitalizeTerm(item.plural),
      empty: "Nothing matches. Try another search or value range.",
      loadMore: STOREFRONT_PAGING_COPY.loadMore,
    },
    rates: {
      step: "Step 3",
      title: "Pull rates",
      description: `Each ${item.singular}'s chance, in percent.`,
      totalLabel: "Total pull rate",
      empty: `Pick at least two ${item.plural} to set their pull rates.`,
      sortLabel: "Sort by value",
      ascending: "Low → high",
      descending: "High → low",
      rateLabel: (itemName) => `Pull rate for ${itemName}`,
      ratePlaceholder: "0.000",
      remove: (itemName) => `Remove ${itemName}`,
    },
    summary: {
      step: "Step 4",
      title: { create: `Create your ${box.singular}`, edit: `Save your ${box.singular}` },
      cutLabel: "Your cut",
      earnings: { before: "You earn", after: "per open. A higher cut raises the price." },
      priceLabel: `${Box} price`,
      submit: {
        name_empty: "Enter a name",
        name_invalid: "Fix the name",
        image: "Select an image",
        items: `Select at least ${FORGE_MIN_ITEMS} ${item.plural}`,
        rates: "Set pull rates",
        total: "Pull rates must total 100%",
        create: links.create,
        save: "Save changes",
      },
      saving: "Saving…",
      cancel: "Cancel",
    },
  };
}

/** The creator's own page's words, by section, in the store's terms. */
export interface ForgeMineCopy {
  heading: {
    /** The trail's stops above the page: Home, then the Forge (also the phones' Back). */
    home: string;
    forge: string;
    title: string;
    intro: string;
    /** The way to the builder. */
    create: string;
  };
  /** What the creator's boxes have earned and not yet claimed, and the claim. */
  earnings: { title: string; description: string; claim: string; claiming: string };
  boxes: {
    title: string;
    /** While the creator has built nothing. */
    empty: string;
    /** A box's badge, by its status. */
    status: Record<MyForgeBox["status"], string>;
    /** The label of each fact a row lists; opens is singular at 1. */
    facts: { price: string; opens: (count: number) => string; cut: string; earned: string };
    /** A row's actions; Archive and Delete also confirm their dialog. */
    actions: { edit: string; archive: string; unarchive: string; delete: string };
    /** What Archive and Delete ask first, given the box's name, and the way out. */
    confirm: {
      archive: { title: (name: string) => string; description: string };
      delete: { title: (name: string) => string; description: string };
      cancel: string;
    };
  };
}

export function defaultForgeMineCopy(terms: StorefrontTerminology): ForgeMineCopy {
  const { box, forge } = terms;
  const links = forgeLinks(terms);
  return {
    heading: {
      home: links.home,
      forge: links.forge,
      title: links.mine,
      intro: `The ${box.plural} you built, and what their opens have earned you.`,
      create: links.create,
    },
    earnings: {
      title: "Unclaimed earnings",
      description: "Your cut of every open, added to your balance when you claim it.",
      claim: "Claim",
      claiming: "Claiming…",
    },
    boxes: {
      title: `Your ${box.plural}`,
      empty: `You have not built any ${box.plural} yet.`,
      status: {
        active: "Live",
        pre_release: "Pre-release",
        archived: "Archived",
        draft: "Draft",
      },
      facts: {
        price: "Price",
        opens: (count) => (count === 1 ? "Open" : "Opens"),
        cut: "Cut",
        earned: "Earned",
      },
      actions: { edit: "Edit", archive: "Archive", unarchive: "Unarchive", delete: "Delete" },
      confirm: {
        archive: {
          title: (name) => `Archive ${name}?`,
          description: `It leaves the ${forge.singular} and nobody can open it until you unarchive it.`,
        },
        delete: {
          title: (name) => `Delete ${name}?`,
          description:
            "Nobody can open it again, and it cannot be undone. What it has already earned you stays yours to claim.",
        },
        cancel: "Cancel",
      },
    },
  };
}
